/**
 * @fileoverview Auth API Client - specialized client for authentication operations
 * Extends BaseApiClient with authentication-specific functionality
 */

import { BaseApiClient } from '../core/BaseApiClient';
import { ApiError } from '../core/errors';
import type { 
  ApiClientConfig, 
  RequestOptions 
} from '../core/types';

// Authentication-specific types
export interface LoginRequest {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface SignupRequest {
  email: string;
  password: string;
  name: string;
  agreedToTerms: boolean;
  subscribeToNewsletter?: boolean;
}

export interface LoginResponse {
  success: boolean;
  token: string;
  refreshToken: string;
  user: UserProfile;
  expiresIn: number;
  requiresTwoFactor?: boolean;
  message?: string;
}

export interface UserProfile {
  id: string;
  email: string;
  name: string;
  tier: 'free' | 'premium' | 'pro';
  createdAt: string;
  lastLogin?: string;
  preferences: {
    theme: 'light' | 'dark' | 'auto';
    notifications: boolean;
    newsletter: boolean;
  };
  limits: {
    strategies: number;
    backtests: number;
    optimizations: number;
  };
  usage: {
    strategies: number;
    backtests: number;
    optimizations: number;
  };
}

export interface RefreshTokenResponse {
  token: string;
  refreshToken: string;
  expiresIn: number;
}

export interface UpdateProfileRequest {
  name?: string;
  preferences?: Partial<UserProfile['preferences']>;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  newPassword: string;
}

export interface VerifyEmailRequest {
  token: string;
}

export class AuthApiClient extends BaseApiClient {
  constructor(config: { baseURL: string } & ApiClientConfig) {
    super(config);
  }

  /**
   * Login user with email and password
   */
  async login(credentials: LoginRequest, options?: RequestOptions): Promise<LoginResponse> {
    try {
      const response = await this.post<LoginResponse>('/auth/login', credentials, options);
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        // Add specific error handling for auth errors
        if (error.statusCode === 401) {
          throw new ApiError('Invalid email or password', 401, 'INVALID_CREDENTIALS');
        }
        if (error.statusCode === 423) {
          throw new ApiError('Account is locked. Please try again later.', 423, 'ACCOUNT_LOCKED');
        }
        throw error;
      }
      throw new ApiError('Login failed', 500, 'LOGIN_ERROR');
    }
  }

  /**
   * Register new user account
   */
  async signup(userData: SignupRequest, options?: RequestOptions): Promise<LoginResponse> {
    try {
      const response = await this.post<LoginResponse>('/auth/signup', userData, options);
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.statusCode === 409) {
          throw new ApiError('Email address is already registered', 409, 'EMAIL_EXISTS');
        }
        if (error.statusCode === 422) {
          throw new ApiError('Invalid user data provided', 422, 'VALIDATION_ERROR');
        }
        throw error;
      }
      throw new ApiError('Registration failed', 500, 'SIGNUP_ERROR');
    }
  }

  /**
   * Refresh authentication token
   */
  async refreshToken(refreshToken: string, options?: RequestOptions): Promise<RefreshTokenResponse> {
    try {
      const response = await this.post<RefreshTokenResponse>(
        '/auth/refresh',
        { refreshToken },
        options
      );
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.statusCode === 401) {
          throw new ApiError('Refresh token is invalid or expired', 401, 'INVALID_REFRESH_TOKEN');
        }
        throw error;
      }
      throw new ApiError('Token refresh failed', 500, 'REFRESH_ERROR');
    }
  }

  /**
   * Logout user (invalidate tokens)
   */
  async logout(options?: RequestOptions): Promise<void> {
    try {
      await this.post<void>('/auth/logout', {}, options);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Logout failed', 500, 'LOGOUT_ERROR');
    }
  }

  /**
   * Get current user profile
   */
  async getProfile(options?: RequestOptions): Promise<UserProfile> {
    try {
      const response = await this.get<UserProfile>('/auth/profile', options);
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.statusCode === 401) {
          throw new ApiError('Authentication required', 401, 'AUTH_REQUIRED');
        }
        throw error;
      }
      throw new ApiError('Failed to get user profile', 500, 'PROFILE_ERROR');
    }
  }

  /**
   * Update user profile
   */
  async updateProfile(updates: UpdateProfileRequest, options?: RequestOptions): Promise<UserProfile> {
    try {
      const response = await this.put<UserProfile>('/auth/profile', updates, options);
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.statusCode === 422) {
          throw new ApiError('Invalid profile data', 422, 'VALIDATION_ERROR');
        }
        throw error;
      }
      throw new ApiError('Failed to update profile', 500, 'PROFILE_UPDATE_ERROR');
    }
  }

  /**
   * Change user password
   */
  async changePassword(passwordData: ChangePasswordRequest, options?: RequestOptions): Promise<void> {
    try {
      await this.put<void>('/auth/password', passwordData, options);
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.statusCode === 401) {
          throw new ApiError('Current password is incorrect', 401, 'INVALID_PASSWORD');
        }
        if (error.statusCode === 422) {
          throw new ApiError('New password does not meet requirements', 422, 'WEAK_PASSWORD');
        }
        throw error;
      }
      throw new ApiError('Failed to change password', 500, 'PASSWORD_CHANGE_ERROR');
    }
  }

  /**
   * Request password reset email
   */
  async forgotPassword(request: ForgotPasswordRequest, options?: RequestOptions): Promise<void> {
    try {
      await this.post<void>('/auth/forgot-password', request, options);
    } catch (error) {
      if (error instanceof ApiError) {
        // Don't reveal if email exists for security
        if (error.statusCode === 404) {
          return; // Silently succeed
        }
        throw error;
      }
      throw new ApiError('Failed to send reset email', 500, 'FORGOT_PASSWORD_ERROR');
    }
  }

  /**
   * Reset password with token
   */
  async resetPassword(resetData: ResetPasswordRequest, options?: RequestOptions): Promise<void> {
    try {
      await this.post<void>('/auth/reset-password', resetData, options);
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.statusCode === 401) {
          throw new ApiError('Reset token is invalid or expired', 401, 'INVALID_RESET_TOKEN');
        }
        if (error.statusCode === 422) {
          throw new ApiError('Password does not meet requirements', 422, 'WEAK_PASSWORD');
        }
        throw error;
      }
      throw new ApiError('Failed to reset password', 500, 'PASSWORD_RESET_ERROR');
    }
  }

  /**
   * Verify email address
   */
  async verifyEmail(verificationData: VerifyEmailRequest, options?: RequestOptions): Promise<void> {
    try {
      await this.post<void>('/auth/verify-email', verificationData, options);
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.statusCode === 401) {
          throw new ApiError('Verification token is invalid or expired', 401, 'INVALID_VERIFICATION_TOKEN');
        }
        throw error;
      }
      throw new ApiError('Failed to verify email', 500, 'EMAIL_VERIFICATION_ERROR');
    }
  }

  /**
   * Resend email verification
   */
  async resendVerification(options?: RequestOptions): Promise<void> {
    try {
      await this.post<void>('/auth/resend-verification', {}, options);
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.statusCode === 409) {
          throw new ApiError('Email is already verified', 409, 'EMAIL_ALREADY_VERIFIED');
        }
        throw error;
      }
      throw new ApiError('Failed to resend verification', 500, 'RESEND_VERIFICATION_ERROR');
    }
  }

  /**
   * Check if email is available for registration
   */
  async checkEmailAvailability(email: string, options?: RequestOptions): Promise<{ available: boolean }> {
    try {
      const response = await this.get<{ available: boolean }>(
        `/auth/check-email?email=${encodeURIComponent(email)}`,
        options
      );
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to check email availability', 500, 'EMAIL_CHECK_ERROR');
    }
  }

  /**
   * Get user's session information
   */
  async getSessionInfo(options?: RequestOptions): Promise<{
    isAuthenticated: boolean;
    user?: UserProfile;
    expiresAt?: string;
  }> {
    try {
      const response = await this.get<{
        isAuthenticated: boolean;
        user?: UserProfile;
        expiresAt?: string;
      }>('/auth/session', options);
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.statusCode === 401) {
          return { isAuthenticated: false };
        }
        throw error;
      }
      throw new ApiError('Failed to get session info', 500, 'SESSION_ERROR');
    }
  }
}
